import React, { useEffect, useState, useRef } from 'react'
import productStore from './stores/product'
import notificationStore from './stores/notification';
import transactionStore from './stores/transaction'
import vendorStore from './stores/vendor'
import locationsStore from './stores/locations'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro' // <-- import styles to be used
import Modal from './components/Modal'
import { Text, Boolean, Select } from './components/Fields'
import Button from './components/Button'
import Table from './components/Table'
import numeral from 'numeral'
import SortableHeader from './components/SortableHeader'
import TableToExcel from './lib/table-to-excel'

const costColumns = [
  {column: 'productCost', key: 'product_cost', label: 'Product Cost'},
  {column: 'brandingCost', key: 'branding_cost', label: 'Branding Cost'},
  {column: 'additionalCost', key: 'additional_cost', label: 'Additional Costs'},
]

function totalCost(product) {
  return costColumns.reduce((total, {key}) => total + (parseFloat(product[key]) || 0), 0)
}

const ProductRow = ({product}) => {
  const [addToTransaction] = transactionStore(state => [state.add])
  const [updateProduct, updateFilter, archiveProduct, visibleColumns] = productStore(state => [state.update, state.updateFilter, state.archive, state.visibleColumns])
  const [editing, setEditing] = useState(false)
  const [productToEdit, setProductToEdit] = useState({})
  const [archiving, setArchiving] = useState(false)

  const [
    vendors,
  ] = vendorStore((state) => [
    state.collection,
  ])

  const [locations] = locationsStore((state) => [state.collection])

  function cancel(e) {
    e.preventDefault()
    setEditing(false)
  }
  async function save(e) {
    e.preventDefault()
    await updateProduct(productToEdit)
    setEditing(false)
  }
  function setExactFilter(e) {
    e.preventDefault()
    updateFilter('parent_title', '=' + product.parent_title)
  }
  function edit(e) {
    e.preventDefault()
    setProductToEdit(product)
    setArchiving(false)
    setEditing(true)
  }
  async function archive(e) {
    e.preventDefault()
    await archiveProduct(product)
    setEditing(false)
  }

  return (
    <Table.Row>
      {visibleColumns.id &&
        <Table.Td>{product.magento_id}</Table.Td>
      }
      {visibleColumns.parent &&
        <Table.Td>
          <a onClick={setExactFilter}>
            {product.parent_title}
          </a>
        </Table.Td>
      }
      {visibleColumns.sku &&
        <Table.Td>{product.sku}</Table.Td>
      }
      {visibleColumns.name &&
        <Table.Td>{product.title}</Table.Td>
      }
      {visibleColumns.vendor &&
        <Table.Td>{product.vendor_title}</Table.Td>
      }
      {visibleColumns.leadTime &&
        <Table.Td className="text-right" data-t="n">{product.lead_time}</Table.Td>
      }
      {locations.map(location => visibleColumns[`threshold-${location.id}`] &&
        <Table.Td className="text-right" data-t="n" key={location.id}>{product[`threshold-${location.id}`]}</Table.Td>
      )}
      {locations.map(location => visibleColumns[`quantity-${location.id}`] &&
        <Table.Td className="text-right" data-t="n" key={location.id}>{product[`quantity-${location.id}`]}</Table.Td>
      )}
      {locations.map(location => visibleColumns[`onorder-${location.id}`] &&
        <Table.Td className="text-right" data-t="n" key={location.id}>{product[`onorder-${location.id}`]}</Table.Td>
      )}
      {visibleColumns.swag &&
        <Table.Td className="text-right">
          {product.swag === true && "yes"}
          {product.swag === false && "no"}
        </Table.Td>
      }
      {visibleColumns.ops &&
        <Table.Td className="text-right">
          {product.ops === true && "yes"}
          {product.ops === false && "no"}
        </Table.Td>
      }
      {visibleColumns.price &&
        <Table.Td className="text-right" data-t="n">{product.price && numeral(product.price).format('$0,0.00')}</Table.Td>
      }
      {costColumns.map(({column, key}) => visibleColumns[column] &&
        <Table.Td className="text-right" data-t="n" key={column}>{product[key] && numeral(product[key]).format('$0,0.00')}</Table.Td>
      )}
      {visibleColumns.cost &&
        <Table.Td className="text-right" data-t="n">{product.cost && numeral(product.cost).format('$0,0.00')}</Table.Td>
      }
      {visibleColumns.edit &&
        <Table.Td data-t="remove">
          <Button
            onClick={edit}
            block={true}>
            <FontAwesomeIcon icon={icon({name: 'pencil'})} />
            Edit
          </Button>
          <Modal isOpen={editing} onClose={() => setEditing(false)}>
            <Modal.Header onClose={() => setEditing(false)}>
              Editing {product.title}
            </Modal.Header>
            <Modal.Body>
              {locations.map(location => {
                const lp = productToEdit?.locations_products_attributes?.find(lp => lp.location_id == location.id) ?? {location_id: location.id, notice_threshold: ''}
                return <div key={location.id}>
                  <Text
                    label={`${location.title} Threshold`}
                    value={lp.notice_threshold}
                    onChange={e => {
                      const newLp = {...lp}
                      newLp.notice_threshold = e.target.value
                      const newLocationsProducts = productToEdit.locations_products_attributes.filter(lp => lp.location_id != location.id).concat(newLp)
                      setProductToEdit({...productToEdit, locations_products_attributes: newLocationsProducts})
                    }}
                    groupClass="mb-4"
                  />
                </div>
              })}
              {costColumns.map(({key, label}) =>
                <Text
                  key={key}
                  label={label}
                  value={productToEdit[key] ?? ''}
                  onChange={e => setProductToEdit({...productToEdit, [key]: e.target.value})}
                  groupClass="mb-4"
                />
              )}
              <div className="mb-4">
                Total Cost: {numeral(totalCost(productToEdit)).format('$0,0.00')}
              </div>
              <Boolean
                label="Swag"
                value={productToEdit.swag ?? ''}
                onChange={e => setProductToEdit({...productToEdit, swag: e.target.value == "true"})}
                groupClass="mb-4"
              />
              <Boolean
                label="OPS"
                value={productToEdit.ops ?? ''}
                onChange={e => setProductToEdit({...productToEdit, ops: e.target.value == "true"})}
                groupClass="mb-4"
              />
              <Select
                label="Vendor"
                value={productToEdit.vendor_id ?? ''}
                onChange={e => setProductToEdit({...productToEdit, vendor_id: e.target.value})}
                groupClass="mb-4"
                options={vendors}
                valueKey="id"
                textKey="title"
              />
              <Text
                label="Lead Time (days)"
                value={productToEdit.lead_time ?? ''}
                onChange={e => setProductToEdit({...productToEdit, lead_time: e.target.value})}
                groupClass="mb-4"
              />
            </Modal.Body>
            <Modal.Footer>
              <div className="flex items-center justify-between gap-4">
                {product.archive ?
                  <>
                    {archiving ?
                      <Button onClick={archive}>
                        <FontAwesomeIcon icon={icon({name: 'question'})} />
                        Are you sure?
                      </Button>
                      :
                      <Button onClick={e => setArchiving(true)}>
                        <FontAwesomeIcon icon={icon({name: 'trash-undo'})} />
                        Restore
                      </Button>
                    }
                  </>
                  :
                  <>
                    {archiving ?
                      <Button type="danger" onClick={archive}>
                        <FontAwesomeIcon icon={icon({name: 'question'})} />
                        Are you sure?
                      </Button>
                      :
                      <Button type="danger" onClick={e => setArchiving(true)}>
                        <FontAwesomeIcon icon={icon({name: 'trash'})} />
                        Archive
                      </Button>
                    }
                  </>
                }
                <div className="flex items-center justify-between gap-4">
                  <a href="#" onClick={cancel}>
                    Cancel
                  </a>
                  <Button type="success" onClick={save}>
                    <FontAwesomeIcon icon={icon({name: 'save'})} />
                    Save
                  </Button>
                </div>
              </div>
            </Modal.Footer>
          </Modal>
        </Table.Td>
      }
      {visibleColumns.add &&
        <Table.Td data-t="remove">
          <Button
            onClick={() => addToTransaction(product)}
            type="success"
            block={true}>
            <FontAwesomeIcon icon={icon({name: 'plus'})} />
            Add
          </Button>
        </Table.Td>
      }
    </Table.Row>
  )
}

export default () => {
  const [editing, setEditing] = useState(false)
  const [noticeThreshold, setNoticeThreshold] = useState({})
  const [costs, setCosts] = useState({})
  const [swag, setSwag] = useState(null)
  const [leadTime, setLeadTime] = useState('')
  const [ops, setOps] = useState(null)
  const [vendorId, setVendorId] = useState('')
  const [archiving, setArchiving] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [columnsVisible, setColumnsVisible] = useState(false)
  const [columns, setColumns] = useState(new Map())
  const columnsRef = useRef()
  const tableRef = useRef()
  const [
    fetchProducts,
    filters,
    updateFilter,
    filteredProducts,
    updateProduct,
    archiveProduct,
    setSort,
    currentSortKey,
    sortDirection,
    visibleColumns,
    toggleVisibleColumn,
  ] = productStore((state) => [
    state.fetch,
    state.filters,
    state.updateFilter,
    state.filtered,
    state.update,
    state.archive,
    state.setSort,
    state.sortKey,
    state.sortDirection,
    state.visibleColumns,
    state.toggleVisibleColumn,
  ]);
  const [
    vendors,
    fetchVendors,
  ] = vendorStore((state) => [
    state.collection,
    state.fetch,
  ])

  const [
    locations,
    fetchLocations,
  ] = locationsStore((state) => [
    state.collection,
    state.fetch,
  ])

  const [addNotification] = notificationStore((state) => [state.add]);
  const [
    addToTransaction,
    setTransactionToEdit,
    locationId,
    setLocationId,
  ] = transactionStore((state) => [
    state.add,
    state.setTransactionToEdit,
    state.locationId,
    state.setLocationId,
  ]);
  useEffect(() => {
    fetchVendors();
    fetchProducts();
    fetchLocations();
  }, []);
  function addAll() {
    filteredProducts().forEach(product => addToTransaction(product))
  }
  async function updateProducts() {
    await filteredProducts().forEach(product => {
      const newProduct = {...product}
      for (const key in noticeThreshold) {
        if (!noticeThreshold[key]) continue
        const locationProduct = newProduct.locations_products_attributes.find(lp => lp.location_id == key) ?? {location_id: key}
        locationProduct.notice_threshold = noticeThreshold[key]
        newProduct.locations_products_attributes = newProduct.locations_products_attributes.filter(lp => lp.location_id != key).concat(locationProduct)
      }
      costColumns.forEach(({key}) => {
        if (costs[key]) {
          newProduct[key] = costs[key]
        }
      })
      newProduct.swag = swag
      newProduct.vendor_id = vendorId
      if (ops) {
        newProduct.ops = ops
      }
      if (leadTime) {
        newProduct.lead_time = leadTime
      }
      updateProduct(newProduct)
    })
    setEditing(false)
  }
  function edit() {
    setArchiving(false)
    setEditing(true)
  }
  async function archive() {
    await filteredProducts().forEach(product => {
      archiveProduct(product)
    })
    setEditing(false)
  }
  async function magentoSync() {
    setIsSyncing(true)
    const response = await fetch('/api/products/pull')
    setIsSyncing(false)
    if (response.ok) {
      addNotification({message: 'Products sync started', type: 'success'})
      fetchProducts()
      return
    } 
    addNotification({message: 'Error syncing products', type: 'error'})
  }

  useEffect(() => {
    const map = new Map()
    map.set('id', 'ID')
    map.set('parent', 'Parent')
    map.set('sku', 'SKU')
    map.set('name', 'Name')
    map.set('vendor', 'Vendor')
    map.set('leadTime', 'Lead Time (days)')
    locations.forEach(location => {
      map.set(`threshold-${location.id}`, `Threshold: ${location.title}`)
    })
    locations.forEach(location => {
      map.set(`quantity-${location.id}`, `Qty: ${location.title}`)
    })
    locations.forEach(location => {
      map.set(`onorder-${location.id}`, `On Order: ${location.title}`)
    })
    map.set('swag', 'Swag')
    map.set('ops', 'OPS')
    map.set('price', 'Price')
    costColumns.forEach(({column, label}) => {
      map.set(column, label)
    })
    map.set('cost', 'Total Cost')
    map.set('edit', 'Edit')
    map.set('add', 'Add')
    setColumns(map)
  }, [locations])

  function handleClick(e) {
    if (columnsRef.current && !columnsRef.current.contains(e.target)) {
      setColumnsVisible(false)
    }
  }
  useEffect(() => {
    if (columnsVisible) {
      setTimeout(() => document.addEventListener('click', handleClick))
    } else {
      document.removeEventListener('click', handleClick)
    }
    return () => document.removeEventListener('click', handleClick)
  }, [columnsVisible])

  function exportTableToCsv() {
    const table = tableRef.current.querySelector('table').cloneNode(true)
    table.querySelectorAll('td[data-t="n"]').forEach(td => {
      td.textContent = numeral(td.textContent).value()
    })
    table.querySelectorAll('[data-t="remove"]').forEach(el => {
      el.remove()
    })
    TableToExcel.convert(table, {
      name: "magic_box_products.xlsx",
      sheet: {
        name: 'Sheet 1',
        ySplit: 1,
      }
    })
  }

  return (
    <div>
      <div className="flex justify-between gap-4 mb-4 items-end">
        <div className='flex gap-4'>
          <h1 className="text-4xl">Products</h1>
          <Button onClick={magentoSync} disabled={isSyncing} >
            <FontAwesomeIcon icon={icon({name: 'rotate'})} spin={isSyncing} />
            Sync Products
          </Button>
          <Button to="/kits">
            <FontAwesomeIcon icon={icon({name: 'toolbox'})} spin={isSyncing} />
            Kits
          </Button>
          <Button to="/locations">
            <FontAwesomeIcon icon={icon({name: 'location-dot'})} />
            Locations
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Select
            onChange={e => setLocationId(e.target.value)}
            value={locationId}
          >
            <option value="">Select Location</option>
            {locations.map(location => <option key={location.id} value={location.id}>{location.title}</option>)}
          </Select>
          <label className="flex items-center">
            <input
              type="radio"
              name="archive"
              checked={!filters.archive}
              onChange={e => updateFilter('archive', false)}
            />
            <span className="ml-2">Live</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="archive"
              checked={filters.archive}
              onChange={e => updateFilter('archive', true)}
            />
            <span className="ml-2">Archive</span>
          </label>
          <div className="relative">
            <Button onClick={() => setColumnsVisible((old) => !old)}>
              {columnsVisible
                ?  <>
                  <FontAwesomeIcon icon={icon({name: 'xmark'})} />
                  Close
                </>
                : <>
                  <FontAwesomeIcon icon={icon({name: 'building-columns'})} />
                  Columns
                </>
              }
            </Button>
            {columnsVisible &&
              <div ref={columnsRef} className="shadow-transaction absolute top-full translate-y-2 right-0 rounded bg-white z-20 p-6 w-64">
                {[...columns].map(([column, name]) =>
                  <label className="flex gap-2 items-center mb-2 cursor-pointer" key={column}>
                    <input
                      type="checkbox"
                      checked={visibleColumns[column]}
                      onChange={e => toggleVisibleColumn(column)}
                     
                    />
                    <span>{name}</span>
                  </label>
                )}
              </div>
            }
          </div>
          <Button onClick={exportTableToCsv}>
            <FontAwesomeIcon icon={icon({name: 'file-excel'})} />
            Export
          </Button>
        </div>
      </div>
      <div ref={tableRef}>
        <Table>
          <Table.Head className="sticky top-0">
            <Table.Row>
              {visibleColumns.id &&
                <SortableHeader sortKey="magento_id" sortType="integer" {...{setSort, currentSortKey, sortDirection}}>ID</SortableHeader>
              }
              {visibleColumns.parent &&
                <SortableHeader sortKey="parent_title" {...{setSort, currentSortKey, sortDirection}}>Parent</SortableHeader>
              }
              {visibleColumns.sku &&
                <SortableHeader sortKey="sku" {...{setSort, currentSortKey, sortDirection}}>SKU</SortableHeader>
              }
              {visibleColumns.name &&
                <SortableHeader sortKey="title" {...{setSort, currentSortKey, sortDirection}}>Name</SortableHeader>
              }
              {visibleColumns.vendor &&
                <SortableHeader sortKey="vendor_title" {...{setSort, currentSortKey, sortDirection}}>Vendor</SortableHeader>
              }
              {visibleColumns.leadTime &&
                <SortableHeader sortKey="lead_time" sortType="integer" {...{setSort, currentSortKey, sortDirection}}>Lead Time (days)</SortableHeader>
              }
              {locations.map(location => visibleColumns[`threshold-${location.id}`] &&
                <SortableHeader key={location.id} sortKey={`threshold-${location.id}`} sortType="integer" {...{setSort, currentSortKey, sortDirection}}>Threshold: {location.title}</SortableHeader>
              )}
              {locations.map(location => visibleColumns[`quantity-${location.id}`] &&
                <SortableHeader key={location.id} sortKey={`quantity-${location.id}`} sortType="integer" {...{setSort, currentSortKey, sortDirection}}>Qty: {location.title}</SortableHeader>
              )}
              {locations.map(location => visibleColumns[`onorder-${location.id}`] &&
                <SortableHeader key={location.id} sortKey={`onorder-${location.id}`} sortType="integer" {...{setSort, currentSortKey, sortDirection}}>On Order: {location.title}</SortableHeader>
              )}
              {visibleColumns.swag &&
                <SortableHeader sortKey="swag" {...{setSort, currentSortKey, sortDirection}}>Swag</SortableHeader>
              }
              {visibleColumns.ops &&
                <SortableHeader sortKey="ops" {...{setSort, currentSortKey, sortDirection}}>OPS</SortableHeader>
              }
              {visibleColumns.price &&
                <Table.Th>Price</Table.Th>
              }
              {costColumns.map(({column, label}) => visibleColumns[column] &&
                <Table.Th key={column}>{label}</Table.Th>
              )}
              {visibleColumns.cost &&
                <Table.Th>Total Cost</Table.Th>
              }
              {visibleColumns.edit &&
                <Table.Th width="145" data-t="remove">
                  <Button
                    onClick={edit}
                    block={true}>
                    <FontAwesomeIcon icon={icon({name: 'pencil'})} />
                    Edit All
                  </Button>
                </Table.Th>
              }
              {visibleColumns.add &&
                <Table.Th width="145" data-t="remove">
                  <Button
                    onClick={addAll}
                    type="success"
                    block={true}>
                    <FontAwesomeIcon icon={icon({name: 'plus'})} />
                    Add All
                  </Button>
                </Table.Th>
              }
            </Table.Row>
            <Table.Row data-t="remove">
              {visibleColumns.id &&
                <Table.Th>
                  <Text
                    type="number"
                    value={filters.magento_id ?? ''}
                    onChange={e => updateFilter('magento_id', e.target.value)}
                  />
                </Table.Th>
              }
              {visibleColumns.parent &&
                <Table.Th>
                  <Text
                    value={filters.parent_title ?? ''}
                    onChange={e => updateFilter('parent_title', e.target.value)}
                  />
                </Table.Th>
              }
              {visibleColumns.sku &&
                <Table.Th>
                  <Text
                    value={filters.sku ?? ''}
                    onChange={e => updateFilter('sku', e.target.value)}
                  />
                </Table.Th>
              }
              {visibleColumns.name &&
                <Table.Th>
                  <Text
                    value={filters.title ?? ''}
                    onChange={e => updateFilter('title', e.target.value)}
                  />
                </Table.Th>
              }
              {visibleColumns.vendor &&
                <Table.Th>
                  <Select
                    value={filters.vendor_id?.slice(1) ?? ''}
                    onChange={e => updateFilter('vendor_id', e.target.value ? '=' + e.target.value.toString() : '')}
                    options={vendors}
                    valueKey="id"
                    textKey="title"
                  />
                </Table.Th>
              }
              {visibleColumns.leadTime &&
                <Table.Th>
                  <Text
                    value={filters.lead_time ?? ''}
                    onChange={e => updateFilter('lead_time', e.target.value)}
                  />
                </Table.Th>
              }
              {locations.map(location => visibleColumns[`threshold-${location.id}`] &&
                <Table.Th key={location.id}>
                  <Text
                    value={filters[`threshold-${location.id}`] ?? ''}
                    onChange={e => updateFilter(`threshold-${location.id}`, e.target.value)}
                  />
                </Table.Th>
              )}
              {locations.map(location => visibleColumns[`quantity-${location.id}`] &&
                <Table.Th key={location.id}>
                  <Text
                    value={filters[`quantity-${location.id}`] ?? ''}
                    onChange={e => updateFilter(`quantity-${location.id}`, e.target.value)}
                  />
                </Table.Th>
              )}
              {locations.map(location => visibleColumns[`onorder-${location.id}`] &&
                <Table.Th key={location.id}>
                  <Text
                    value={filters[`onorder-${location.id}`] ?? ''}
                    onChange={e => updateFilter(`onorder-${location.id}`, e.target.value)}
                  />
                </Table.Th>
              )}
              {visibleColumns.swag &&
                <Table.Th></Table.Th>
              }
              {visibleColumns.ops &&
                <Table.Th></Table.Th>
              }
              {visibleColumns.price &&
                <Table.Th>
                  <Text
                    value={filters.price ?? ''}
                    onChange={e => updateFilter('price', e.target.value)}
                  />
                </Table.Th>
              }
              {costColumns.map(({column, key}) => visibleColumns[column] &&
                <Table.Th key={column}>
                  <Text
                    value={filters[key] ?? ''}
                    onChange={e => updateFilter(key, e.target.value)}
                  />
                </Table.Th>
              )}
              {visibleColumns.cost &&
                <Table.Th>
                  <Text
                    value={filters.cost ?? ''}
                    onChange={e => updateFilter('cost', e.target.value)}
                  />
                </Table.Th>
              }
              {visibleColumns.edit &&
                <Table.Th data-t="remove"></Table.Th>
              }
              {visibleColumns.add &&
                <Table.Th data-t="remove"></Table.Th>
              }
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {filteredProducts().map((product) => (
              <ProductRow product={product} key={product.id} />
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row data-t="remove">
              {visibleColumns.id &&
                <Table.Th>
                </Table.Th>
              }
              {visibleColumns.parent &&
                <Table.Th>
                </Table.Th>
              }
              {visibleColumns.sku &&
                <Table.Th>
                </Table.Th>
              }
              {visibleColumns.name &&
                <Table.Th>
                </Table.Th>
              }
              {visibleColumns.vendor &&
                <Table.Th>
                </Table.Th>
              }
              {visibleColumns.leadTime &&
                <Table.Th>
                </Table.Th>
              }
              {locations.map(location => visibleColumns[`threshold-${location.id}`] &&
                <Table.Th key={location.id}>
                </Table.Th>
              )}
              {locations.map(location => visibleColumns[`quantity-${location.id}`] &&
                <Table.Th key={location.id}>
                </Table.Th>
              )}
              {locations.map(location => visibleColumns[`onorder-${location.id}`] &&
                <Table.Th key={location.id}>
                </Table.Th>
              )}
              {visibleColumns.swag &&
                <Table.Th></Table.Th>
              }
              {visibleColumns.ops &&
                <Table.Th></Table.Th>
              }
              {visibleColumns.price &&
                <Table.Th>{numeral(filteredProducts().reduce((total, p) => total + parseFloat(p.price ?? '0') * p.quantity, 0)).format('$0,0.00')}</Table.Th>
              }
              {costColumns.map(({column, key}) => visibleColumns[column] &&
                <Table.Th key={column}>{numeral(filteredProducts().reduce((total, p) => total + parseFloat(p[key] ?? '0') * p.quantity, 0)).format('$0,0.00')}</Table.Th>
              )}
              {visibleColumns.cost &&
                <Table.Th>{numeral(filteredProducts().reduce((total, p) => total + parseFloat(p.cost ?? '0') * p.quantity, 0)).format('$0,0.00')}</Table.Th>
              }
              {visibleColumns.edit &&
                <Table.Th data-t="remove"></Table.Th>
              }
              {visibleColumns.add &&
                <Table.Th data-t="remove"></Table.Th>
              }
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
      <Modal isOpen={editing} onClose={() => setEditing(false)}>
        <Modal.Header onClose={() => setEditing(false)}>
          Editing {filteredProducts().length} products
        </Modal.Header>
        <Modal.Body>
          {locations.map(location => <div key={location.id}>
            <Text
              label={`${location.title} Threshold`}
              value={noticeThreshold[location.id] ?? ''}
              onChange={e => setNoticeThreshold({...noticeThreshold, [location.id]: e.target.value})}
              groupClass="mb-4"
            />
          </div>)}
          {costColumns.map(({key, label}) =>
            <Text
              key={key}
              label={label}
              value={costs[key] ?? ''}
              onChange={e => setCosts({...costs, [key]: e.target.value})}
              groupClass="mb-4"
            />
          )}
          <Boolean
            label="Swag"
            value={swag}
            onChange={e => setSwag(e.target.value == 'true')}
            groupClass="mb-4"
          />
          <Boolean
            label="OPS"
            value={ops}
            onChange={e => setOps(e.target.value == 'true')}
            groupClass="mb-4"
          />
          <Select
            label="Vendor"
            value={vendorId}
            onChange={e => setVendorId(e.target.value)}
            groupClass="mb-4"
            options={vendors}
            valueKey="id"
            textKey="title"
          />
          <Text
            label="Lead Time (days)"
            value={leadTime}
            onChange={e => setLeadTime(e.target.value)}
            groupClass="mb-4"
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="flex items-center justify-between gap-4">
            {filters.archive ?
              <>
                {archiving ?
                  <Button onClick={archive}>
                    <FontAwesomeIcon icon={icon({name: 'question'})} />
                    Really restore {filteredProducts().length} products?
                  </Button>
                  :
                  <Button onClick={e => setArchiving(true)}>
                    <FontAwesomeIcon icon={icon({name: 'trash-undo'})} />
                    Restore
                  </Button>
                }
              </>
              :
              <>
                {archiving ?
                  <Button type="danger" onClick={archive}>
                    <FontAwesomeIcon icon={icon({name: 'question'})} />
                    Really archive {filteredProducts().length} products?
                  </Button>
                  :
                  <Button type="danger" onClick={e => setArchiving(true)}>
                    <FontAwesomeIcon icon={icon({name: 'trash'})} />
                    Archive
                  </Button>
                }
              </>
            }
            <div className="flex items-center justify-between gap-4">
              <a href="#" onClick={() => setEditing(false)}>
                Cancel
              </a>
              <Button type="success" onClick={updateProducts}>
                <FontAwesomeIcon icon={icon({name: 'save'})} />
                Save
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
